import { rest } from 'msw';

const URL_BASE = `${process.env.REACT_APP_BASE_URL_SUBSCRIPTIONS_API}/`;

/**
 * Dummy `stores[].concepts` in the shape the Complementos contract proposes.
 * The live API still sends `{ key, value }`, so this handler is what feeds the concepts table
 * until the backend catches up.
 */
const concept = (
  key: string,
  name: string,
  value: number,
  overrides: { description?: string; quantity?: number | null; periodStart?: string; periodEnd?: string } = {},
) => ({
  key,
  name,
  value,
  description: overrides.description ?? null,
  quantity: overrides.quantity === undefined ? 1 : overrides.quantity,
  periodStart: overrides.periodStart ?? null,
  periodEnd: overrides.periodEnd ?? null,
});

const PLAN_ALL_IN_ONE = concept('plan', 'Suscripción All In One', 4657, { description: 'Mensualidad de tu plan' });

const CARD = {
  uuid: 'pm-001',
  provider: 'STRIPE',
  providerId: 'pi_001',
  providerPublicId: '4242',
  providerPublicBrand: 'visa',
  providerPublicMonth: '12',
  providerPublicYear: '2028',
  methodType: 'CARD',
  orgUuid: 'org-001',
  status: 'ACTIVE',
};

const INVOICE_DATA = {
  uuid: 'inv-001',
  orgUuid: 'org-001',
  businessName: 'Restaurante Azul S.A. de C.V.',
  firstName: null,
  firstLastName: null,
  secondLastName: null,
  postalCode: '06700',
  taxId: 'RAZ010203AB9',
  taxInvoiceUse: 'G03',
  email: 'facturacion@restauranteazul.mx',
  taxRegime: '601',
};

const block = <T extends Record<string, unknown>>(overrides: T) => ({
  extensionPos: 0,
  extensionConnect: 0,
  paymentDate: '2026-03-10',
  interval: 'MONTHLY',
  status: 'ACTIVE',
  subStatus: 'PAID',
  customerState: 'ACTIVE',
  advanceInvoice: false,
  groupInvoice: true,
  configEnabled: true,
  paymentMethod: CARD,
  invoiceData: INVOICE_DATA,
  lastError: null,
  discounts: null,
  inactiveSince: null,
  ...overrides,
});

/**
 * Three subscriptions, each covering a different case of the summary tab: the grouped table with a
 * discount, the flat list of a single store, and a block with a failed charge and no fiscal record.
 * More than one block also keeps the single-subscription redirect from firing.
 */
const BLOCKS = [
  block({
    uuid: 'blk-centro',
    name: 'Región Centro',
    total: 14728.84,
    stores: [
      {
        storeUuid: 'store-001',
        storeName: 'Parrot Cuauhtémoc',
        plan: 'All In One',
        concepts: [
          PLAN_ALL_IN_ONE,
          concept('caja_extra', 'Caja Extra', 1160, { description: 'Cajas adicionales para cobrar', quantity: 2 }),
          concept('discounts', 'Descuento', -500, { quantity: null }),
        ],
      },
      {
        storeUuid: 'store-002',
        storeName: 'Parrot Narvarte',
        plan: 'Pro',
        concepts: [concept('plan', 'Suscripción Pro', 3596, { description: 'Mensualidad de tu plan' })],
      },
      {
        storeUuid: 'store-003',
        storeName: 'Parrot Nápoles',
        plan: 'All In One',
        concepts: [
          PLAN_ALL_IN_ONE,
          concept('parrot_grow', 'Parrot Grow', 1158.84, {
            description: 'Conversaciones con tus clientes desde Parrot · 500 conversaciones al mes',
            periodStart: '2026-03-01',
            periodEnd: '2026-03-31',
          }),
        ],
      },
    ],
  }),
  block({
    uuid: 'blk-roma',
    name: 'Parrot Roma',
    total: 5237,
    groupInvoice: false,
    stores: [
      {
        storeUuid: 'store-004',
        storeName: 'Parrot Roma',
        plan: 'All In One',
        concepts: [
          PLAN_ALL_IN_ONE,
          concept('caja_extra', 'Caja Extra', 580, { description: 'Caja adicional para cobrar en tu sucursal' }),
        ],
      },
    ],
  }),
  block({
    uuid: 'blk-polanco',
    name: 'Parrot Polanco',
    total: 4657,
    status: 'PAYMENT_REQUIRED',
    subStatus: 'PENDING',
    invoiceData: null,
    lastError: { code: 'expired_card', concept: 'Suscripción All In One', date: '2026-03-10' },
    stores: [{ storeUuid: 'store-005', storeName: 'Parrot Polanco', plan: 'All In One', concepts: [PLAN_ALL_IN_ONE] }],
  }),
];

const envelope = (data: unknown) => ({ data, datetime: new Date().toISOString() });

/** The organization's subscriptions. Three of them, so the single-subscription redirect stays off. */
export const listSubscriptionBlocksWithConcepts = rest.get(`${URL_BASE}v1/subscription-block/`, (_req, res, ctx) =>
  res(ctx.status(200), ctx.json(envelope(BLOCKS))),
);

/** One subscription by uuid. An unknown uuid falls back to the first, so staging links still resolve. */
export const getSubscriptionBlockWithConcepts = rest.get(
  `${URL_BASE}v1/subscription-block/:blockUuid`,
  (req, res, ctx) => {
    const match = BLOCKS.find((item) => item.uuid === req.params.blockUuid);

    return res(ctx.status(200), ctx.json(envelope(match ?? { ...BLOCKS[0], uuid: req.params.blockUuid as string })));
  },
);
